import { GA_SOORT_NAAM, gaArtikelVelden, gaBasisSlug, gaatMetEigenTransport, type CatalogOverrides } from "@glastotaal/core";
import { getArticle, getArticles } from "@/lib/catalog";
import type { ProductionOp, ConfigInput, RoosterOrderLine, ShowerInput } from "@glastotaal/core";
import type { GlassEditState } from "@/features/configurator/useConfigurator";
import type { BengConfigInput } from "@/features/benglas/benglas-engine";
import type { IsoConfig } from "@/features/isolatieglas/isolatieglas-engine";
import type { GAItem } from "@/lib/track";

/** Configuratie van een ventilatierooster — de server herprijst hieruit (zonder aantal). */
export type RoosterLineConfig = Omit<RoosterOrderLine, "quantity">;

interface CartDisplay {
  title: string;
  lines: string[];
  imageUrl?: string;
}

/**
 * Een toeslag/onderdeel dat bij één geconfigureerd product hoort (bv. bedieningskoord of
 * de eenmalige instelkost voor een speciale RAL-kleur). Hoort bij de regel zelf — wordt
 * dus mee toegevoegd/verwijderd en kan niet los uit de winkelwagen worden gehaald.
 */
export interface CartSurcharge {
  label: string;
  /** Bedrag excl. BTW in centen. Per stuk (× aantal), tenzij `once`. */
  amountCents: number;
  /** Eenmalig per regel (schaalt niet met het aantal), bv. een instelkost. */
  once?: boolean;
  /**
   * Eenmalig per ORDER, gegroepeerd op deze sleutel: een toeslag met dezelfde `onceKey`
   * wordt over de hele winkelwagen maar één keer gerekend (bv. de speciale-RAL-instelkost
   * per kleur — meerdere roosters in dezelfde kleur betalen 'm samen één keer). Telt niet
   * mee in de regelprijs; wordt apart als "eenmalige kosten" getoond.
   */
  onceKey?: string;
}

/**
 * A cart line. Stores IDs + (for glass) the ConfigInput ONLY — never amounts
 * (recomputed for display, authoritatively by the server at checkout).
 * Discriminated on `kind`: glas op maat vs. fixed-price article.
 */
export interface GlassCartItem {
  kind: "glass";
  id: string;
  productSlug: string;
  subcategory: string;
  variantId: string;
  config: ConfigInput;
  grindLengthMm?: number;
  isCustom: true;
  /** Geserialiseerde maatvoering-tekening (SVG) voor op de factuur/bestelbon. */
  drawingSvg?: string;
  /** Bewerkingen mét coördinaten voor de productietekening (zie production-ops.ts). */
  productionOps?: ProductionOp[];
  /** Klant wil het glas láten plaatsen → montage-offerte via partner ProGlass (geen betaling
   *  aan ons; de bestelling wordt een doorverwezen offerte). */
  montage?: boolean;
  /** Losslesse UI-state om deze regel vanuit de winkelwagen te kunnen wijzigen (configurator herladen). */
  editState?: GlassEditState;
  /** Koppelt gekoppelde regels (bv. een ventilatierooster bij dit glas) — samen wijzigen/verwijderen. */
  groupId?: string;
  /** Bericht van de klant bij deze regel (installatieglas) — gaat mee naar de bestelling. */
  note?: string;
  /** Foto's van de situatie bij dat bericht (Storage-URL's). */
  photos?: string[];
  display: CartDisplay;
}

export interface ArticleCartItem {
  kind: "article";
  id: string;
  articleSlug: string;
  /** Slug van de PRODUCTPAGINA (basis-artikel) — nodig om vanuit de winkelwagen te wijzigen,
   *  want articleSlug kan een variant- of synthetische (rooster/voorzetraam) slug zijn. */
  pageSlug?: string;
  quantity: number;
  /** Display-only price snapshot (excl. BTW, cents); server reprices at checkout. */
  priceCents: number;
  isCustom: false;
  /** Vrije spec-notitie (configureerbaar artikel) — meegestuurd naar de bestelling. */
  note?: string;
  /** Op-lengte-artikel (U-profiel): gekozen lengte in mm; priceCents = prijs/meter × lengte. */
  lengthMm?: number;
  /** Voorzetraam (STORM_WINDOW): nominale maat; server herprijst basis + grootte-/energietoeslag. */
  wMm?: number;
  hMm?: number;
  /** Prijs op aanvraag: geen online prijs/betaling — gaat mee in een offerte-aanvraag. */
  onRequest?: boolean;
  /** Láten plaatsen → montage-offerte via partner ProGlass (geen betaling aan ons). Bij artikelen
   *  gebruikt voor configureerbare producten die je kunt laten monteren (bv. ventilatieroosters). */
  montage?: boolean;
  /** Vaste toeslagen/onderdelen die bij dit product horen (koord, eenmalige instelkost, …). */
  surcharges?: CartSurcharge[];
  /** Rooster-configuratie: aanwezig bij configureerbare roosters → server herprijst hieruit. */
  roosterConfig?: RoosterLineConfig;
  /** Koppelt dit artikel aan een glasregel (bv. een gekoppeld ventilatierooster) — samen wijzigen. */
  groupId?: string;
  display: CartDisplay;
}

/** Losslesse UI-state om een BENGglas-regel vanuit de winkelwagen te kunnen wijzigen. */
export interface BengEditState {
  line: string;
  thickness: number | null;
  widthStr: string;
  heightStr: string;
  paneMode: "" | "gelaagd";
  foil: string;
  hybrid: "" | "hybride" | "hybride-gelaagd";
  spouwMm: number;
  /** Afstandhouder in de hybride spouw ("standaard" | "warm-edge"); ontbreekt bij
   *  regels van vóór 01-08-2026 → die vallen terug op de standaardkeuze. */
  spacer?: string;
  shapeId: string;
  dimStrs: { dim1: string; dim2: string; dim3: string; dim4: string };
  rotationDeg: 0 | 90 | 180 | 270;
  quantity: number;
  montage: boolean;
}

export interface BenglasCartItem {
  kind: "benglas";
  id: string;
  config: BengConfigInput;
  /** Display-only price snapshot (excl. BTW, cents); server reprices at checkout. */
  priceCents: number;
  isCustom: true;
  /** Láten plaatsen → montage-offerte via partner ProGlass (geen betaling aan ons). */
  montage?: boolean;
  /** Losslesse UI-state om deze regel vanuit de winkelwagen te wijzigen. */
  editState?: BengEditState;
  /** Maatvoeringstekening uit de configurator, voor de productietekening. */
  drawingSvg?: string;
  display: CartDisplay;
}

/** Losslesse UI-state om een isolatie-/triple-glasregel vanuit de winkelwagen te wijzigen. */
export interface IsoEditState {
  /** true = triple (3 ruiten, TripleConfigurator); false = dubbel (IsolatieglasConfigurator). */
  triple: boolean;
  uitvoering: string;
  klima: string;
  outer: string;
  inner: string;
  /** Middenruit (alleen triple). */
  middle?: string;
  spouwMm: number;
  /** Type afstandhouder ("alu" | "tgi"); oudere items dragen nog een kleur-id. */
  spacerColor: string;
  /** UV-bestendige siliconen randafdichting (los bovenop de afstandhouder). */
  siliconen?: boolean;
  shapeId: string;
  /** Vorm-maten (width/height/dim1..4) uit useVormDims. */
  dims: Record<string, string>;
  rotationDeg: 0 | 90 | 180 | 270;
  quantity: number;
  montage: boolean;
}

export interface IsolatieglasCartItem {
  kind: "isolatieglas";
  id: string;
  config: IsoConfig;
  /** Display-only price snapshot (excl. BTW, cents); server reprices at checkout. */
  priceCents: number;
  isCustom: true;
  /** Láten plaatsen → montage-offerte via partner ProGlass (geen betaling aan ons). */
  montage?: boolean;
  /** Losslesse UI-state om deze regel vanuit de winkelwagen te wijzigen. */
  editState?: IsoEditState;
  /** Maatvoeringstekening uit de configurator, voor de productietekening. */
  drawingSvg?: string;
  display: CartDisplay;
}

/** Losslesse UI-state om een douche-regel vanuit de winkelwagen te wijzigen. */
export interface ShowerEditState {
  config: ShowerInput;
  quantity: number;
}

export interface ShowerCartItem {
  kind: "shower";
  id: string;
  config: ShowerInput;
  /** Display-only price snapshot (excl. BTW, cents); server reprices at checkout. */
  priceCents: number;
  isCustom: true;
  /** Losslesse UI-state om deze regel vanuit de winkelwagen te wijzigen. */
  editState?: ShowerEditState;
  display: CartDisplay;
}

export type CartItem = GlassCartItem | ArticleCartItem | BenglasCartItem | IsolatieglasCartItem | ShowerCartItem;

/** Een montage-regel: de klant wil dit product láten plaatsen → de regel gaat als offerte naar
 *  partner ProGlass (die het glas + de plaatsing prijst). Geldt voor enkel glas, isolatieglas/triple,
 *  BENGglas én configureerbare artikelen (ventilatieroosters). */
export function isMontageLine(item: CartItem): boolean {
  if (item.kind === "glass" || item.kind === "isolatieglas" || item.kind === "benglas") return item.montage === true;
  if (item.kind === "article") return item.montage === true;
  return false;
}

/** Bevat de winkelwagen een montage-item → er hoort (mede) een ProGlass-offerte-doorverwijzing bij. */
export function cartHasMontage(items: CartItem[]): boolean {
  return items.some(isMontageLine);
}

/** Voorzetraam (op maat, met wMm/hMm): wordt met de Timmers-rekwagen bezorgd → glas-transporttarief. */
export function isStormWindowItem(item: CartItem): boolean {
  return item.kind === "article" && item.wMm != null && item.hMm != null;
}

/**
 * Gaat deze regel met de vrachtwagen mee in plaats van met de pakketdienst? Dan telt hij voor de
 * verzendkosten (en levertijd) als glas. Glaslatten automatisch (2 tot 5,5 m, 28-07-2026); verder
 * wat in Productbeheer op "eigen transport" staat — per artikel, groep of subgroep, zie
 * gaatMetEigenTransport. Een kleurvariant of maat-regel volgt zijn basisartikel.
 */
export function isVrachtItem(item: CartItem, overrides: CatalogOverrides | null | undefined): boolean {
  if (item.kind !== "article") return false;
  const art = getArticle(basisArtikelSlug(gaBasisSlug(item.articleSlug))) ?? getArticle(item.articleSlug);
  // De gekozen lengte telt mee: boven 2500 mm past het niet in een pakket, hoe het artikel ook
  // ingesteld staat. Zonder lengte-keuze kijkt de kern naar de handelslengte van het artikel.
  return gaatMetEigenTransport(art, overrides, item.lengthMm);
}

/**
 * Een "offerte-regel": geen online prijs en geen betaling — de prijs vervalt en de regel gaat mee
 * in een offerte-aanvraag. Dat geldt voor prijs-op-aanvraag-artikelen (offerte via ons) én voor
 * montage-regels (offerte via ProGlass, die eigen tarieven hanteert). Overal in de winkelwagen/
 * checkout behandelen we deze regels als "op aanvraag": geen bedrag, uitgesloten van het totaal.
 */
export function isQuoteLine(item: CartItem): boolean {
  if (isMontageLine(item)) return true;
  if (item.kind === "article") return item.onRequest === true;
  return false;
}

/** Quantity of a line regardless of kind. */
export function lineQuantity(item: CartItem): number {
  if (item.kind === "glass") return item.config.quantity;
  if (item.kind === "benglas" || item.kind === "isolatieglas" || item.kind === "shower") return item.config.quantity;
  return item.quantity;
}

/**
 * Map een winkelwagenregel naar een GA4-item. `item_id` = artikel-slug (of anders het `kind`),
 * prijs in euro's. `unitPriceEuros` overschrijft de prijs met de per-stuk-prijs (glas-op-maat
 * heeft géén `priceCents` op de regel — die komt uit `cartItemPrice`); zonder dat valt 'ie terug
 * op de opgeslagen `priceCents`. Zo blijven de funnel-events (view_cart, begin_checkout, …)
 * consistent met de bestaande add_to_cart/purchase-mapping.
 */
/**
 * GA4-identiteit van een winkelwagenregel — dezelfde als bij bekijken (configurator/productpagina)
 * en bij de serverzijdige aankoop (packages/core/src/ga4-item.ts). De winkelwagen- en afrekenstap
 * stuurden eerst "glass" met de maten in de naam; daardoor sloot geen enkele stap op de volgende aan.
 */
/**
 * Basisartikel van een kleurvariant, uit dezelfde variantThumbs als de server (basisArtikelVan).
 * Varianten staan zelf niet in de catalogus van de browser, dus zonder dit werd elke kleur in GA4
 * een eigen product. De catalogus laadt async: bij een andere omvang opnieuw opbouwen.
 */
let basisPerVariant: Map<string, string> | null = null;
let basisBron = 0;
function basisArtikelSlug(slug: string): string {
  const artikelen = getArticles();
  if (!basisPerVariant || basisBron !== artikelen.length) {
    basisPerVariant = new Map();
    for (const a of artikelen) {
      for (const v of a.variantThumbs ?? []) {
        if (v.slug && v.slug !== a.slug && !basisPerVariant.has(v.slug)) basisPerVariant.set(v.slug, a.slug);
      }
    }
    basisBron = artikelen.length;
  }
  return basisPerVariant.get(slug) ?? slug;
}

// Ook voor een regel die nog geen id heeft (bij het toevoegen aan de winkelwagen).
type ZonderId<T> = T extends unknown ? Omit<T, "id"> : never;
/**
 * `item_category` hoort hier óók bij. Bij het BEKIJKEN stuurden we hem wel mee, bij in-winkelwagen
 * en bij de aankoop niet — gevolg: 296 verkochte stuks en € 4.535 omzet stonden in GA4 onder
 * "(not set)" en je kon glas niet van artikelen scheiden zonder op productnaam te matchen
 * (gemeten 30-10-2026). Zelfde waarde als view_item gebruikt, anders krijg je twee reeksen naast
 * elkaar: voor een artikel de categorie uit de catalogus, voor glas de productSlug.
 */
export function gaIdentiteit(item: CartItem | ZonderId<CartItem>): { item_id: string; item_name: string; item_category?: string } {
  const anyItem = item as { articleSlug?: string };
  if (anyItem.articleSlug) {
    // Basisartikel: een rooster of voorzetraam heeft per maat/kleur een eigen slug (…__1200x800) en
    // de naam in de winkelwagen draagt de kleur. GA4 moet dat als één product zien — gelijk aan de
    // productpagina en aan wat de server bij de aankoop vastlegt.
    const basis = basisArtikelSlug(gaBasisSlug(anyItem.articleSlug));
    const a = getArticle(basis);
    const v = gaArtikelVelden(basis, a?.name, a?.articleNumber);
    return { item_id: v.gaItemId, item_name: a ? v.gaItemName : item.display.title, item_category: a?.category };
  }
  if (item.kind === "glass")
    return { item_id: item.variantId, item_name: item.subcategory || item.display.title, item_category: item.productSlug };
  // De overige configurators (isolatieglas, vacuümglas, douche) hebben geen catalogus-categorie;
  // hun `kind` is precies de familie die je in een rapport wilt zien.
  return { item_id: item.kind, item_name: GA_SOORT_NAAM[item.kind] ?? item.display.title, item_category: item.kind };
}

export function cartItemToGA(item: CartItem, unitPriceEuros?: number): GAItem {
  const anyItem = item as { articleSlug?: string; priceCents?: number };
  const price = unitPriceEuros ?? (typeof anyItem.priceCents === "number" ? anyItem.priceCents / 100 : undefined);
  return {
    ...gaIdentiteit(item),
    price,
    quantity: lineQuantity(item),
  };
}
